import type React from 'react';
import { type FC, type ReactNode, useEffect, useState } from 'react';

import classNames from 'classnames';

import VARIANTS from '../../constants/variants';
import IconButton from '../icon_button/icon_button';

import styles from './search_input.module.css';

type Props = {
  name?: string;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  value?: string;
  placeholder?: string;
  onSearch: (value: string) => void;
  autoFocus?: boolean;
  searchButton?: boolean;
  canClear?: boolean;
  iconButtonClassName?: boolean;
  clearButtonClassName?: string;
  rightContent?: ReactNode;
};

const SearchInput: FC<Props> = (props) => {
  const {
    className,
    placeholder = 'Search...',
    autoFocus,
    onSearch,
    searchButton = true,
  } = props;

  const [value, setValue] = useState(props.value ?? '');

  useEffect(() => {
    setValue(props.value ?? '');
  }, [props.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onSearch(event.target.value);
    setValue(event.target.value);
  };

  const handleClear = (): void => {
    setValue('');
    onSearch('');
  };

  const handleSubmit = (): void => {
    onSearch(value);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    // prevent from submitting form when hitting enter
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  };

  return (
    <div className={classNames(styles.component, className)}>
      {searchButton && (
        <IconButton
          className={props.buttonClassName}
          iconName="search"
          onClick={handleSubmit}
          variant={VARIANTS.WHITE}
        />
      )}
      <input
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        autoFocus={autoFocus}
        className={classNames(styles.input, props.inputClassName, {
          [styles.input_with_right_content]: Boolean(props.rightContent),
        })}
        name={props.name || 'query'}
        inputMode="search"
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        aria-label="Search"
        role="searchbox"
      />
      {props.rightContent}
      {value.length !== 0 && props.canClear && (
        <IconButton
          iconName="deletion_clear"
          iconSizeUAD="xs"
          className={classNames(styles.cross_icon, props.clearButtonClassName)}
          size={16}
          name="Clear"
          role="button"
          onClick={handleClear}
        />
      )}
    </div>
  );
};

export default SearchInput;
